'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Loader2, CheckCircle2, ArrowRight } from 'lucide-react'

const CATEGORY_OPTIONS = [
  'Tops', 'Bottoms', 'Dresses', 'Outerwear', 'Activewear',
  'Formalwear', 'Streetwear', 'Accessories',
]

export default function ApparelApplyPage() {
  const [form, setForm] = useState({
    brand_name: '',
    contact_name: '',
    email: '',
    website: '',
    catalog_size: '',
    message: '',
    company_fax: '', // honeypot
  })
  const [categories, setCategories] = useState<string[]>([])
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const set = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }))

  const toggleCategory = (c: string) =>
    setCategories(cs => cs.includes(c) ? cs.filter(x => x !== c) : [...cs, c])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setState('sending')
    setErrorMsg('')
    try {
      const res = await fetch('/api/apparel/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, categories: categories.join(', ') }),
      })
      const data = await res.json()
      if (res.ok && data.ok) {
        setState('done')
      } else {
        setState('error')
        setErrorMsg(data.errors?.join('; ') || 'Submission failed — please try again.')
      }
    } catch {
      setState('error')
      setErrorMsg('Network error — please try again.')
    }
  }

  const inputCls =
    'w-full rounded-lg border border-gray-700 bg-slate-900 px-3 py-2.5 text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none'

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-7xl mx-auto flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link href="/" className="text-white font-bold text-lg">
            Link<span className="text-emerald-400">Box</span> AI
          </Link>
          <Link href="/apparel" className="text-gray-400 hover:text-white text-sm font-medium transition-colors">
            ← Apparel partners
          </Link>
        </div>
      </nav>

      <div className="mx-auto max-w-2xl px-4 pt-32 pb-24 sm:px-6">
        <h1 className="text-3xl md:text-4xl font-bold text-white">Apply for partnership</h1>
        <p className="mt-3 text-gray-400">
          Tell us about your brand. We&apos;ll follow up with partnership and
          advertising options and get your first pieces into the dressing room.
        </p>

        {state === 'done' ? (
          <div className="mt-10 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-8 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
            <h2 className="mt-4 text-xl font-semibold text-white">Application received</h2>
            <p className="mt-2 text-gray-400">
              Thanks — we&apos;ll be in touch at {form.email}.
            </p>
            <Link href="/apparel" className="mt-6 inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300">
              Back to Apparel partners <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm text-gray-300">Brand name *</label>
                <input required minLength={2} className={inputCls} value={form.brand_name} onChange={set('brand_name')} placeholder="Acme Apparel Co." />
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-gray-300">Contact name *</label>
                <input required minLength={2} className={inputCls} value={form.contact_name} onChange={set('contact_name')} placeholder="Jamie Rivera" />
              </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm text-gray-300">Work email *</label>
                <input required type="email" className={inputCls} value={form.email} onChange={set('email')} placeholder="jamie@acmeapparel.com" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-gray-300">Website</label>
                <input type="url" className={inputCls} value={form.website} onChange={set('website')} placeholder="https://acmeapparel.com" />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm text-gray-300">Categories</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_OPTIONS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCategory(c)}
                    className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                      categories.includes(c)
                        ? 'border-emerald-500 bg-emerald-500/15 text-emerald-300'
                        : 'border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-200'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm text-gray-300">Catalog size</label>
              <input className={inputCls} value={form.catalog_size} onChange={set('catalog_size')} placeholder="e.g. 40 SKUs, two seasonal drops a year" />
            </div>

            <div>
              <label className="mb-1.5 block text-sm text-gray-300">Anything else</label>
              <textarea rows={4} className={inputCls} value={form.message} onChange={set('message')} placeholder="Lines you'd want draped first, advertising goals, timelines…" />
            </div>

            {/* honeypot — hidden from real users */}
            <input
              type="text"
              value={form.company_fax}
              onChange={set('company_fax')}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            {state === 'error' && (
              <p className="text-sm text-red-400">{errorMsg}</p>
            )}

            <button
              type="submit"
              disabled={state === 'sending'}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 font-semibold text-slate-950 hover:bg-emerald-400 transition-colors disabled:opacity-60"
            >
              {state === 'sending' && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit application
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
